// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@tps/App/Components/Common/XngZone";
import Notify from "@tps/App/Components/Notify";
import xLoadingOverlay from "@tps/App/Handlers/XNG/LoadingOverlay";
import { CustomAddAccountApiParameter } from "@tps/App/Services/CreateAccountService";
import { useValidate } from "@tps/XDL/Utilities/xValidate";
import React, { RefObject, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// XNG-CHANGES
// This is fully customized widget

const mapDispatchToProps = {
    addAccount,
    signIn,
    validatePassword,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    // XNG-Changes
    loadCurrentCountries,
    xLoadingOverlay,
};

const mapStateToProps = (state: ApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
    // XNG-Changes
    countries: getCurrentCountries(state),
});

enum fields {
    termsOfUseLink = "termsOfUseLink",
    privacyPolicyLink = "privacyPolicyLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.termsOfUseLink]: LinkFieldValue;
        [fields.privacyPolicyLink]: LinkFieldValue;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CreateAccountStyles {
    form?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    userGridItem?: GridItemProps;
    userGridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    userNameGridItem?: GridItemProps;
    userNameTextField?: TextFieldPresentationProps;
    passwordGridItem?: GridItemProps;
    passwordTextField?: TextFieldPresentationProps;
    passwordIcon?: IconPresentationProps;
    confirmPasswordGridItem?: GridItemProps;
    confirmPasswordTextField?: TextFieldPresentationProps;
    confirmPasswordIcon?: IconPresentationProps;
    subscriptionGridItem?: GridItemProps;
    submitErrorGridItem?: GridItemProps;
    submitErrorTitle?: TypographyProps;
    submitGridItem?: GridItemProps;
    reCaptchaGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requirementsGridItem?: GridItemProps;
    requirementsTitle?: TypographyProps;
    passwordRequirementsGridItem?: GridItemProps;
    passwordRequirementsGridContainer?: GridContainerProps;
    passwordGridContainer?: GridContainerProps;
    // XNG-Changes
    container?: GridContainerProps;
    pageTitleGridItem?: GridItemProps;
    formGridItem?: GridItemProps;
    formFieldGridItem?: GridItemProps;
    existingCustomerGridItem?: GridItemProps;
    existingCustomerRadioGroup?: RadioGroupProps;
    formFieldTooltip?: TooltipPresentationProps;
    companyNameTextField?: TextFieldPresentationProps;
    emailAddressTextField?: TextFieldPresentationProps;
    firstNameTextField?: TextFieldPresentationProps;
    lastNameTextField?: TextFieldPresentationProps;
    phoneTextField?: TextFieldPresentationProps;
    roleTextField?: TextFieldPresentationProps;
    addressTextField?: TextFieldPresentationProps;
    cityTextField?: TextFieldPresentationProps;
    stateSelect?: SelectPresentationProps;
    postalCodeTextField?: TextFieldPresentationProps;
    contactNameTextField?: TextFieldPresentationProps;
    contactFaxTextField?: TextFieldPresentationProps;
    createAccountMessage?: GridItemProps;
    acceptCreateAccountText?: TypographyProps;
    errorMsg?: InjectableCss;
}

export const createAccountStyles: CreateAccountStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    userGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 100px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    userGridContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 300px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    emailGridItem: {
        width: 12,
    },
    userNameGridItem: {
        width: 12,
    },
    passwordGridItem: {
        width: 12,
    },
    confirmPasswordGridItem: {
        width: 12,
    },
    subscriptionGridItem: {
        width: 12,
    },
    submitErrorGridItem: {
        width: 12,
    },
    submitErrorTitle: {
        color: "danger",
    },
    reCaptchaGridItem: { width: 12 },
    submitGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    requirementsGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding-left: 50px;
                        `,
                    ],
                    "min",
                )}/* stylelint-disable-line declaration-block-single-line-max-declarations */
        `,
    },
    passwordRequirementsGridContainer: {
        gap: 5,
    },
    passwordRequirementsGridItem: {
        width: 12,
    },
    requirementsTitle: {
        variant: "h4",
        as: "h2",
    },
};

const styles = createAccountStyles;
const StyledForm = getStyledWrapper("form");

const existingCustomerValidators = {
    companyName: "required|comapnyName",
    email: "required|email|max:60",
    firstName: "required|firstName",
    lastName: "required|lastName",
    city: "required|city",
};
const newCustomerValidators = {
    companyName: "required|comapnyName",
    address: "required|address",
    city: "required|city",
    state: "required",
    postalCode: "required|postalCode:US",
    contactName: "required|fullName",
    email: "required|email|max:60",
    phone: "required|phone",
    fax: "phone",
    role: "required",
};

const CreateAccount = ({
    addAccount,
    signIn,
    validatePassword,
    validateReCaptcha,
    accountSettings,
    returnUrl,
    countries,
    fields,
    id,
    loadCurrentCountries,
    xLoadingOverlay,
}: Props) => {
    const [validators, setValidators] = useState<Dictionary<string>>(existingCustomerValidators);
    const [isExistingCustomer, setIsExistingCustomer] = useState("true");
    const isExistingCustomerSelcted = isExistingCustomer.toLowerCase() === "true";
    const [country, setCountry] = useState<CountryModel>();
    const [errorMessage, setErrorMessage] = useState("");
    const [accountCreated, setAccountCreated] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();
    const { isInShell } = useShellContext();
    const termsOfUseLinkUrl = useGetLink(fields.termsOfUseLink)?.url || "";
    const privacyPolicyLinkUrl = useGetLink(fields.privacyPolicyLink)?.url || "";
    // XNG-Changes
    const [accountCreatedMessage, setAccountCreatedMessage] = useState("");

    useEffect(() => {
        if (isExistingCustomerSelcted) {
            setValidators(existingCustomerValidators);
        } else {
            setValidators(newCustomerValidators);
        }
    }, [isExistingCustomerSelcted]);

    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        }
        if (countries?.length) {
            const country = countries.find(c => c.abbreviation?.toLowerCase() === "us") || countries[0];
            setCountry(country);
        }
    }, [countries]);

    const { field, touched, errors, handleSubmit, isSubmitting, dirty, values, resetForm } = useValidate({
        initialValues: {
            isExistingCustomer: true,
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            role: "",
            companyName: "",
            address: "",
            city: "",
            state: country?.states?.[0]?.abbreviation,
            postalCode: "",
            contactName: "",
            fax: "",
        },
        validators,
        onSubmit: (fieldValues: CustomAddAccountApiParameter, { setSubmitting }: any) => {
            submitHandler(fieldValues, setSubmitting);
        },
    });

    const submitHandler = async (fieldValues: CustomAddAccountApiParameter, setSubmitting: any) => {
        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }

        setSubmitting(true);
        xLoadingOverlay({ key: "add", id: "Create_Account" });

        const existingCustomerForm = {
            firstName: fieldValues.firstName,
            lastName: fieldValues.lastName,
        };

        const newCustomerForm = {
            address: fieldValues.address,
            state: fieldValues.state,
            postalCode: fieldValues.postalCode,
            contactName: fieldValues.contactName,
            fax: fieldValues.fax,
            phone: fieldValues.phone,
            role: fieldValues.role,
        };

        addAccount({
            isCompanyExist: isExistingCustomer,
            email: fieldValues.email,
            companyName: fieldValues.companyName,
            city: fieldValues.city,
            ...(isExistingCustomerSelcted ? existingCustomerForm : newCustomerForm),

            onSuccess: (result: any) => {
                setSubmitting(false);
                xLoadingOverlay({ key: "clear", id: "Create_Account" });
                setErrorMessage("");
                setAccountCreated(true);
                resetForm();
                // XNG-Changes
                if (result?.result?.emailModel?.multipleCustomers) {
                    setAccountCreatedMessage(`${siteMessage("Custom_UserHasMoreThanOneCustomer")}`);
                } else {
                    setAccountCreatedMessage(result?.result?.message || "");
                }
            },
            onError: (errorMessage: string) => {
                setSubmitting(false);
                xLoadingOverlay({ key: "clear", id: "Create_Account" });
                setErrorMessage(errorMessage);
            },
        } as any);
    };

    // show confirmation content when form is successfully submitted
    if (accountCreated) {
        return (
            <>
                <XngZone hint="This Zone is for AccountCreated Confirmation" zoneName="AccountCreated" contentId={id} />
                <StyledWrapper>
                    {/* XNG-Changes */}
                    {accountCreatedMessage || siteMessage("Custom_CreateAccount_Confirmation_Message")}
                </StyledWrapper>
            </>
        );
    }

    // those fields will show in both forms isExistingCustomer true/false
    const sharedFields = {
        companyName: (
            <GridItem {...styles.formFieldGridItem}>
                <TextField
                    {...styles.companyNameTextField}
                    placeholder={translate("Enter Company Name")}
                    label={translate("Company Name")}
                    {...field("companyName")}
                />
            </GridItem>
        ),
        email: (
            <GridItem {...styles.formFieldGridItem}>
                <TextField
                    {...styles.emailAddressTextField}
                    placeholder={translate("eg. example@gmail.com")}
                    label={
                        <>
                            {isExistingCustomerSelcted ? translate("Email") : translate("Contact Email")}
                            {isExistingCustomerSelcted && (
                                <Tooltip
                                    {...styles.formFieldTooltip}
                                    text={siteMessage("Custom_CreateAccount_Email_Message") as string}
                                />
                            )}
                        </>
                    }
                    {...field("email")}
                />
            </GridItem>
        ),
        city: (
            <GridItem {...styles.formFieldGridItem}>
                <TextField
                    {...styles.cityTextField}
                    placeholder={translate("Your City")}
                    label={translate("City")}
                    {...field("city")}
                />
            </GridItem>
        ),
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.pageTitleGridItem}>
                <XngZone contentId={id} zoneName="Content01" hint="Zone For Page Title" />
            </GridItem>
            <GridItem {...styles.formGridItem}>
                <StyledForm {...styles.form} onSubmit={handleSubmit} noValidate>
                    <GridContainer {...styles.userGridContainer}>
                        <GridItem {...styles.existingCustomerGridItem}>
                            <RadioGroup
                                name="haveAccount"
                                label={siteMessage("Custom_Create_Account_User_Have_Account_With_TPS")}
                                value={isExistingCustomer}
                                onChangeHandler={e => {
                                    setIsExistingCustomer(e.currentTarget.value);
                                }}
                                horizontal={true}
                                {...styles.existingCustomerRadioGroup}
                            >
                                <Radio value="true">{translate("Yes")}</Radio>
                                <Radio value="false">{translate("No")}</Radio>
                            </RadioGroup>
                        </GridItem>
                        {isExistingCustomer === "true" && (
                            <>
                                {sharedFields.email}
                                {sharedFields.companyName}
                                {sharedFields.city}
                                <GridItem {...styles.formFieldGridItem}>
                                    <TextField
                                        {...styles.firstNameTextField}
                                        placeholder={translate("First Name")}
                                        label={translate("First Name")}
                                        {...field("firstName")}
                                    />
                                </GridItem>
                                <GridItem {...styles.formFieldGridItem}>
                                    <TextField
                                        {...styles.lastNameTextField}
                                        placeholder={translate("Last Name")}
                                        label={translate("Last Name")}
                                        {...field("lastName")}
                                    />
                                </GridItem>
                            </>
                        )}
                        {isExistingCustomer === "false" && (
                            <>
                                {sharedFields.companyName}
                                <GridItem {...styles.formFieldGridItem}>
                                    <TextField
                                        {...styles.addressTextField}
                                        placeholder={translate("Your Address")}
                                        label={translate("Address")}
                                        {...field("address")}
                                    />
                                </GridItem>
                                {sharedFields.city}
                                {countries && (
                                    <GridItem {...styles.formFieldGridItem}>
                                        <Select
                                            label={translate("State")}
                                            {...styles.stateSelect}
                                            data-test-selector="state"
                                            {...field("state")}
                                        >
                                            <option value="">{translate("Select State")}</option>
                                            {country?.states?.map(o => (
                                                <option key={o.abbreviation} value={o.abbreviation}>
                                                    {o.name}
                                                </option>
                                            ))}
                                        </Select>
                                    </GridItem>
                                )}
                                <GridItem {...styles.formFieldGridItem}>
                                    <TextField
                                        {...styles.postalCodeTextField}
                                        placeholder={translate("Postal Code")}
                                        label={translate("Postal Code")}
                                        {...field("postalCode")}
                                    />
                                </GridItem>
                                <GridItem {...styles.formFieldGridItem}>
                                    <TextField
                                        {...styles.contactNameTextField}
                                        placeholder={translate("Contact Name")}
                                        label={translate("Contact Name")}
                                        {...field("contactName")}
                                    />
                                </GridItem>
                                {sharedFields.email}
                                <GridItem {...styles.formFieldGridItem}>
                                    <TextField
                                        {...styles.phoneTextField}
                                        placeholder={translate("xxx-xxx-xxxx")}
                                        label={
                                            isExistingCustomerSelcted ? translate("Phone") : translate("Contact Phone")
                                        }
                                        {...field("phone")}
                                    />
                                </GridItem>
                                <GridItem {...styles.formFieldGridItem}>
                                    <TextField
                                        {...styles.contactFaxTextField}
                                        placeholder={translate("xxx-xxx-xxxx")}
                                        label={translate("Contact Fax")}
                                        {...field("fax")}
                                    />
                                </GridItem>
                                <GridItem {...styles.formFieldGridItem}>
                                    <TextField
                                        {...styles.roleTextField}
                                        placeholder={translate("Enter Company Role")}
                                        label={translate("Role within the Company")}
                                        {...field("role")}
                                    />
                                </GridItem>
                            </>
                        )}
                        <GridItem {...styles.createAccountMessage}>
                            <Typography {...styles.acceptCreateAccountText}>
                                {siteMessage("Custom_Create_Account_Message", termsOfUseLinkUrl, privacyPolicyLinkUrl)}
                            </Typography>
                        </GridItem>
                        {errorMessage && (
                            <GridItem data-test-selector="createAccount_ErrorMessage" {...styles.submitErrorGridItem}>
                                <Notify {...styles.errorMsg} type="danger">
                                    {errorMessage}
                                </Notify>
                            </GridItem>
                        )}
                        <GridItem {...styles.reCaptchaGridItem}>
                            <ReCaptcha recaptchaRef={recaptchaRef} location="CreateAccount" />
                        </GridItem>
                        <GridItem {...styles.submitGridItem}>
                            <Button
                                {...styles.submitButton}
                                type="submit"
                                disabled={isSubmitting}
                                data-test-selector="createAccount_createButton"
                                aria-label="Register"
                            >
                                {translate("Register")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </StyledForm>
            </GridItem>
            {isInShell && (
                <>
                    <XngZone
                        hint="This Zone is for Account Created Confirmation Content"
                        zoneName="AccountCreated"
                        contentId={id}
                    />
                    <StyledWrapper>{siteMessage("Custom_CreateAccount_Confirmation_Message")}</StyledWrapper>
                </>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CreateAccount),
    definition: {
        allowedContexts: ["CreateAccountPage"],
        group: "Create Account",
        fieldDefinitions: [
            {
                name: fields.termsOfUseLink,
                displayName: "Terms Of Use Url",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
            {
                name: fields.privacyPolicyLink,
                displayName: "Privacy Policy Url",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
